import { useEffect, useRef, useState } from "react";

interface Props {
  src: string;
  alt: string;
  caption?: string;
  onClose: () => void;
}

const MIN_SCALE = 1;
const MAX_SCALE = 6;

const clamp = (n: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, n));

/// Full-screen zoom/pan viewer for the knot step photos, which are too
/// detailed to read at grid size. Opens on tap, supports wheel/pinch
/// zoom and drag/touch pan, and closes on the X, the backdrop, or Esc.
/// Pointer events cover mouse + single-finger pan + two-finger pinch
/// from one code path.
export function ImageLightbox({ src, alt, caption, onClose }: Props) {
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);

  // Active pointers (id -> last position) so we can tell a one-finger
  // pan from a two-finger pinch.
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchStart = useRef<{ dist: number; scale: number } | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // Lock background scroll while the viewer is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const reset = () => {
    setScale(1);
    setTx(0);
    setTy(0);
  };

  const twoPointerDist = () => {
    const pts = [...pointers.current.values()];
    if (pts.length < 2) return 0;
    const a = pts[0]!;
    const b = pts[1]!;
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      pinchStart.current = { dist: twoPointerDist(), scale };
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size >= 2 && pinchStart.current) {
      const next = clamp(
        (pinchStart.current.scale * twoPointerDist()) / pinchStart.current.dist,
        MIN_SCALE,
        MAX_SCALE,
      );
      setScale(next);
      if (next === 1) {
        setTx(0);
        setTy(0);
      }
      return;
    }

    // Single-pointer pan — only meaningful when zoomed in.
    if (scale > 1) {
      setTx((t) => t + (e.clientX - prev.x));
      setTy((t) => t + (e.clientY - prev.y));
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
  };

  const onWheel = (e: React.WheelEvent) => {
    const next = clamp(
      scale * (e.deltaY < 0 ? 1.2 : 1 / 1.2),
      MIN_SCALE,
      MAX_SCALE,
    );
    setScale(next);
    if (next === 1) {
      setTx(0);
      setTy(0);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        style={{
          position: "absolute",
          // Offset by the safe-area insets so the button clears the
          // iPhone status bar / Dynamic Island (the viewport is
          // `viewport-fit=cover`, so the overlay runs under them).
          top: "calc(env(safe-area-inset-top, 0px) + 12px)",
          right: "calc(env(safe-area-inset-right, 0px) + 12px)",
          width: 44,
          height: 44,
          fontSize: 24,
          lineHeight: 1,
          color: "#fff",
          background: "rgba(255,255,255,0.15)",
          border: "1px solid rgba(255,255,255,0.4)",
          borderRadius: 8,
          cursor: "pointer",
        }}
      >
        ✕
      </button>

      <img
        src={src}
        alt={alt}
        draggable={false}
        // Stop backdrop-close + let the gesture handlers own the touch.
        onClick={(e) => e.stopPropagation()}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
        onDoubleClick={() => (scale > 1 ? reset() : setScale(2.5))}
        style={{
          maxWidth: "92vw",
          maxHeight: "82vh",
          objectFit: "contain",
          transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
          transformOrigin: "center center",
          cursor: scale > 1 ? "grab" : "zoom-in",
          touchAction: "none",
          userSelect: "none",
        }}
      />

      {caption && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="small"
          style={{
            marginTop: 12,
            maxWidth: "92vw",
            textAlign: "center",
            color: "#fff",
          }}
        >
          {caption}
        </div>
      )}

      <div
        className="small"
        style={{ marginTop: 8, color: "rgba(255,255,255,0.6)" }}
      >
        Pinch or scroll to zoom · drag to move · tap outside to close
      </div>
    </div>
  );
}
